import React from 'react'
import { Box, IconButton } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/pt-br'

// Invisible but clickable opener so picker still opens when clicked
const EmptyButton: React.ComponentType<any> = (props: any) => (
	<IconButton
		{...props}
		sx={{ ...(props?.sx || {}), opacity: 0 }}
	/>
)

/**
 * ## SchedulesDateNavigation component
 * Date navigation component with previous/next buttons and date select.
 * Combines three elements: previous button, date select, and next button.
 *
 * @param {Dayjs} value - Current selected date
 * @param {(date: Dayjs) => void} onChange - Handler for date changes
 * @returns {JSX.Element} The rendered SchedulesDateNavigation component
 */
interface SchedulesDateNavigationProps {
	value: Dayjs
	onChange: (date: Dayjs) => void
}

const SchedulesDateNavigation = React.memo(function SchedulesDateNavigation({
	value,
	onChange,
}: SchedulesDateNavigationProps) {
	const [pickerOpen, setPickerOpen] = React.useState(false)

	const handlePreviousDay = () => {
		onChange(value.subtract(1, 'day'))
	}

	const handleNextDay = () => {
		onChange(value.add(1, 'day'))
	}

	const handleDateChange = (newValue: Dayjs | null) => {
		if (newValue) {
			onChange(newValue)
		}
	}

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 0,
			}}
		>
			{/* Botão Anterior */}
			<IconButton
				size='small'
				onClick={handlePreviousDay}
				sx={{
					padding: '5px',
					width: '34px',
					height: '34px',
					color: '#00A5FF',
					borderRadius: '100px',
					'&:hover': {
						backgroundColor: 'rgba(0, 165, 255, 0.08)',
					},
				}}
				aria-label='Dia anterior'
			>
				<ChevronLeftIcon sx={{ width: '24px', height: '24px' }} />
			</IconButton>

			{/* Campo de Data */}
			<LocalizationProvider
				adapterLocale='pt-br'
				dateAdapter={AdapterDayjs}
			>
				<DatePicker
					open={pickerOpen}
					onOpen={() => setPickerOpen(true)}
					onClose={() => setPickerOpen(false)}
					value={value}
					onChange={handleDateChange}
					format='ddd - DD/MM/YYYY'
					slots={{ openPickerButton: EmptyButton }}
					slotProps={{
						textField: {
							size: 'small',
							label: 'Dia',
							InputLabelProps: {
								shrink: true,
							},
							InputProps: {
								readOnly: true,
								onClick: () => setPickerOpen(true),
							},
							sx: {
								width: '160px',
								'& .MuiOutlinedInput-root': {
									height: '40px',
									fontFamily: 'Poppins',
									cursor: 'pointer',
									backgroundColor: '#FFFFFF',
									position: 'relative',
									'& input': {
										fontFamily: 'Poppins',
										fontWeight: 500,
										fontSize: '13px',
										color: '#092056',
										padding: '12px 12px 8px',
										lineHeight: '20px',
										textTransform: 'lowercase',
										cursor: 'pointer',
										backgroundColor: '#FFFFFF',
									},
									'& fieldset': {
										borderColor: 'rgba(134, 149, 175, 0.3)',
									},
									'&:hover': {
										backgroundColor: '#FFFFFF',
										'& fieldset': {
											borderColor: 'rgba(134, 149, 175, 0.3)',
										},
									},
									'&.Mui-focused': {
										backgroundColor: '#FFFFFF',
										'& fieldset': {
											borderColor: 'rgba(134, 149, 175, 0.3)',
										},
									},
								},
								'& .MuiInputLabel-root': {
									fontFamily: 'Poppins',
									fontWeight: 400,
									fontSize: '11px',
									lineHeight: '12px',
									color: '#092056',
									backgroundColor: '#FFFFFF',
									padding: '0 4px',
									transform: 'translate(12px, -6px) scale(1)',
									transformOrigin: 'top left',
								},
								'& .MuiInputAdornment-root': {
									position: 'absolute',
									left: 0,
									top: 0,
									height: '100%',
									width: '100%',
									margin: 0,
									maxHeight: 'none',
									pointerEvents: 'none',
									'& .MuiIconButton-root': {
										width: '100%',
										height: '100%',
										margin: 0,
										padding: 0,
										minWidth: 'unset',
										pointerEvents: 'auto',
										'& svg': {
											display: 'none',
										},
									},
								},
							},
						},
					}}
				/>
			</LocalizationProvider>

			{/* Botão Próximo */}
			<IconButton
				size='small'
				onClick={handleNextDay}
				sx={{
					padding: '5px',
					width: '34px',
					height: '34px',
					color: '#00A5FF',
					borderRadius: '100px',
					'&:hover': {
						backgroundColor: 'rgba(0, 165, 255, 0.08)',
					},
				}}
				aria-label='Próximo dia'
			>
				<ChevronRightIcon sx={{ width: '24px', height: '24px' }} />
			</IconButton>
		</Box>
	)
})

export default SchedulesDateNavigation
